/**
 * WHAT A PAYOUT DESTINATION IS, in the console's words.
 *
 * The GUARANTEE lives on the server (`tkt-backend/src/utils/payoutDestination.ts`), because two
 * apps create these — this console and the seller dashboard in mtn-moments — and a rule enforced
 * in one client is a rule the other breaks. This file is the console's half: which fields to
 * collect, what to call them, and what never to send.
 *
 * `bankName` and `accountNumber` are shared storage keys that mean a different thing per kind —
 * the bank vs the telco vs the chain; the account number vs the phone number vs the address. That
 * is why every surface must read them through `LABELS` rather than print "Bank name" over a phone
 * number, and why a destination that says `mobile_money` must never carry `branch` or `swift`.
 */

/**
 * WHAT A PAYOUT LANDS IN, by where the account is.
 *
 * A destination's currency is a fact about its country, not a platform default. The onboarding
 * wizard used to open every payout account with `currency: 'UGX'` — including for the Kenyan
 * clubs that are most of this tenant — which is the same fallback this codebase already refuses
 * in `fmtMoney` ("an amount without its currency is not a partial truth, it is a different
 * fact"). Unknown country → null, and the caller says so rather than guessing.
 */
const COUNTRY_CURRENCY = { Uganda: 'UGX', Kenya: 'KES', Tanzania: 'TZS', Rwanda: 'RWF' };
export const settlementCurrencyOf = (country) => COUNTRY_CURRENCY[String(country || '').trim()] || null;

export const DESTINATION_KINDS = {
  bank_account: {
    label: 'Bank account',
    provider: 'Bank',
    number: 'Account number',
    owns: ['bankName', 'branch', 'swift', 'bankCode', 'accountName', 'accountNumber', 'currency', 'status'],
  },
  mobile_money: {
    label: 'Mobile money',
    provider: 'Provider',
    number: 'Phone number',
    // No branch, no SWIFT, no bank code — a wallet is reached by phone.
    owns: ['bankName', 'accountName', 'accountNumber', 'currency', 'status'],
  },
  crypto: {
    label: 'Crypto wallet',
    provider: 'Network',
    number: 'Wallet address',
    owns: ['bankName', 'accountNumber', 'currency', 'status'],
  },
};

export const kindOf = (type) => DESTINATION_KINDS[type] || DESTINATION_KINDS.bank_account;

/**
 * The fields this kind actually has, trimmed — everything else stays in the browser.
 *
 * The form keeps ONE flat bag of fields for all three kinds, so filling in a bank and then
 * switching the picker to Mobile money leaves the bank's branch and SWIFT sitting in state.
 * `createPaymentAccount({ ...form })` then saved them, and the payout screen read `type` for one
 * line and `bankName` for the next: "Mobile money" above "Absa bank", over an Airtel number.
 * Projecting here is what makes the switch mean what it looks like it means.
 */
export const projectDestination = (type, form) => {
  const owns = kindOf(type).owns;
  const out = {};
  for (const key of owns) {
    if (!(key in form)) continue;
    const v = form[key];
    out[key] = typeof v === 'string' ? v.trim() : v;
  }
  return out;
};

/**
 * The detail keys a stored record carries that its own kind cannot have. Blank values assert
 * nothing, so they are not a contradiction. Used to tell the truth about rows written before the
 * rule existed, rather than quietly rendering half of them.
 */
export const foreignDetailKeys = (type, details) => {
  if (!details || typeof details !== 'object' || !DESTINATION_KINDS[type]) return [];
  const owns = new Set(kindOf(type).owns);
  return Object.entries(details)
    .filter(([k, v]) => !owns.has(k) && String(v ?? '').trim() !== '')
    .map(([k]) => k);
};
